/**
 * Screenshot of the user's screen for the HaulierMagic Assistant (CU-14zefhy0dgu contract G). Taken only after the
 * user allows it (per chat) or presses the camera button; the chat popover and its backdrop are left out, password
 * fields and anything marked `data-ai-private` are blanked, and the image is kept ≤ 1600 px wide and ≤ 1 MB.
 *
 * QA fix D2 (same capturer as RM, wave contract): rendered with modern-screenshot (SVG foreignObject, so the browser
 * itself paints modern CSS colours such as `color()` / `oklch()` that made html2canvas throw), only the visible
 * viewport is drawn (content below / right of it is not even cloned, which is what made big lists take minutes), the
 * result is a JPEG (q 0.8), and the whole capture gives up after 15 s so the assistant is told instead of waiting.
 *
 * MAP-HM: HERE (WebGL) maps read back blank, so each registered map on screen gets a HERE-captured still image laid over
 * its canvas for the duration of the capture (see assistant-map-snapshot.ts; nothing global, no preserveDrawingBuffer).
 */

import {isSensitiveName} from 'app/blocks/service/assistant-context/assistant-context';
import {MapSnapshots, overlayMapSnapshots} from 'app/blocks/service/assistant-context/assistant-map-snapshot';

export const SCREENSHOT_MAX_WIDTH = 1600;
/** D-SHOT: big viewports are drawn smaller (about 1600 x 900 px of canvas at most) so painting and encoding stay quick */
export const SCREENSHOT_MAX_PIXELS = 1600 * 900;
export const SCREENSHOT_MAX_BYTES = 1024 * 1024;
/** hard limit for one capture (render + encode); on expiry the request is declined with a reason */
export const SCREENSHOT_TIMEOUT_MS = 15000;
export const SCREENSHOT_JPEG_QUALITY = 0.75;
/** D-SHOT: one picture / background image may take this long to fetch; after that it is drawn as a blank */
export const SCREENSHOT_IMAGE_TIMEOUT_MS = 1500;
/** D-SHOT: at most this many images are fetched for one capture; any more are drawn blank */
export const SCREENSHOT_MAX_IMAGES = 40;
/** D-SHOT: how long the capture waits for pictures still loading on the page (lazy images below the fold never do) */
export const SCREENSHOT_IMAGE_WAIT_MS = 1000;
/** 1 x 1 transparent GIF drawn in place of an image that was skipped or failed */
export const BLANK_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/**
 * CU-14zefhy21rx H10/H12: the CSS properties copied onto each cloned node (modern-screenshot `includeStyleProperties`).
 * By default every computed property (~400) is read, compared and written per node, which was most of the 15-40 s on a
 * 100-row list (2,000+ nodes) and made the SVG it paints huge; these are the ones that change what a picture looks like
 * (layout, box, borders, colours, text, flex / grid, SVG paint). Measured on hm-staging Organisations / Haulier Orders
 * (100 rows, scrolled halfway, 1600 x 1000): 9-14 s -> 0.6-0.9 s, same picture.
 */
export const SCREENSHOT_STYLE_PROPERTIES: readonly string[] = [
    'display',
    'position',
    'top',
    'right',
    'bottom',
    'left',
    'z-index',
    'float',
    'clear',
    'box-sizing',
    'width',
    'height',
    'min-width',
    'min-height',
    'max-width',
    'max-height',
    'aspect-ratio',
    'zoom',
    'margin-top',
    'margin-right',
    'margin-bottom',
    'margin-left',
    'padding-top',
    'padding-right',
    'padding-bottom',
    'padding-left',
    'border-top-width',
    'border-right-width',
    'border-bottom-width',
    'border-left-width',
    'border-top-style',
    'border-right-style',
    'border-bottom-style',
    'border-left-style',
    'border-top-color',
    'border-right-color',
    'border-bottom-color',
    'border-left-color',
    'border-top-left-radius',
    'border-top-right-radius',
    'border-bottom-left-radius',
    'border-bottom-right-radius',
    'border-collapse',
    'border-spacing',
    'overflow-x',
    'overflow-y',
    'clip',
    'clip-path',
    'visibility',
    'opacity',
    'color',
    'background-color',
    'background-image',
    'background-size',
    'background-position-x',
    'background-position-y',
    'background-repeat',
    'background-clip',
    'background-origin',
    'box-shadow',
    'outline-color',
    'outline-style',
    'outline-width',
    'outline-offset',
    'appearance',
    '-webkit-appearance',
    'font-family',
    'font-size',
    'font-weight',
    'font-style',
    'font-variant',
    'font-variant-numeric',
    'font-feature-settings',
    'font-stretch',
    'line-height',
    'letter-spacing',
    'word-spacing',
    'text-align',
    'text-indent',
    'text-transform',
    'text-decoration-line',
    'text-decoration-color',
    'text-decoration-style',
    'text-decoration-thickness',
    'text-underline-offset',
    'text-overflow',
    'text-shadow',
    'white-space',
    'white-space-collapse',
    'text-wrap-mode',
    'word-break',
    'overflow-wrap',
    'vertical-align',
    'direction',
    'writing-mode',
    '-webkit-line-clamp',
    '-webkit-box-orient',
    'list-style-type',
    'list-style-position',
    'list-style-image',
    'table-layout',
    'caption-side',
    'empty-cells',
    'flex-direction',
    'flex-wrap',
    'flex-grow',
    'flex-shrink',
    'flex-basis',
    'order',
    'justify-content',
    'justify-items',
    'justify-self',
    'align-content',
    'align-items',
    'align-self',
    'row-gap',
    'column-gap',
    'grid-template-columns',
    'grid-template-rows',
    'grid-template-areas',
    'grid-auto-flow',
    'grid-auto-columns',
    'grid-auto-rows',
    'grid-column-start',
    'grid-column-end',
    'grid-row-start',
    'grid-row-end',
    'transform',
    'transform-origin',
    'translate',
    'rotate',
    'scale',
    'filter',
    'backdrop-filter',
    'mix-blend-mode',
    'isolation',
    'mask-image',
    'mask-size',
    'mask-position',
    'mask-repeat',
    '-webkit-mask-image',
    '-webkit-mask-size',
    '-webkit-mask-position',
    '-webkit-mask-repeat',
    'fill',
    'fill-opacity',
    'fill-rule',
    'stroke',
    'stroke-width',
    'stroke-opacity',
    'stroke-linecap',
    'stroke-linejoin',
    'stroke-dasharray',
    'object-fit',
    'object-position'
];

/** CU-14zefhy21rx H10: attribute that carries a stuck sticky element's offset (`dx,dy`) from the live page to its clone. */
export const STUCK_ATTRIBUTE = 'data-ai-shot-stuck';

/** Thrown when a capture took longer than `SCREENSHOT_TIMEOUT_MS`. */
export class ScreenshotTimeoutError extends Error {
    constructor() {
        super('The screenshot took too long');
        this.name = 'ScreenshotTimeoutError';
    }
}

/** Rejects with `ScreenshotTimeoutError` when `work` does not settle within `ms`. */
export function withTimeout<T>(work: Promise<T>, ms: number): Promise<T> {
    let timer: any;
    const limit = new Promise<T>((_, reject) => {
        timer = setTimeout(() => reject(new ScreenshotTimeoutError()), ms);
    });
    return Promise.race([work, limit]).finally(() => clearTimeout(timer));
}

/** Short reason sent with the decline when a capture fails, so the assistant knows why it got no picture. */
export function captureFailureReason(error: any): string {
    if (error instanceof ScreenshotTimeoutError || error?.name === 'ScreenshotTimeoutError') {
        return 'screenshot timed out';
    }
    return 'screenshot failed';
}

/**
 * True for an element that lies entirely below or right of the visible viewport: it is left out of the cloned page so
 * a long list is not rendered row by row. Elements above or left of the viewport are kept (removing them would move
 * what the user sees), and so are fixed / sticky elements, empty wrappers and table cells right of the viewport
 * (dropping a cell would re-flow the visible columns).
 */
export function isOffViewport(
    rect: {top: number; left: number; height: number; width: number},
    viewport: {width: number; height: number},
    position?: string,
    tagName?: string
): boolean {
    if (!rect || position === 'fixed' || position === 'sticky') {
        return false;
    }
    if (rect.width === 0 && rect.height === 0) {
        return false; // display: contents / empty wrappers can hold visible children
    }
    if (rect.top >= viewport.height + 50) {
        return true;
    }
    const cell = /^(td|th|col|colgroup)$/i.test(tagName || '');
    return !cell && rect.left >= viewport.width + 50;
}

/** A screen request from the task status (`screenRequest`), as the chat keeps it. */
export interface ScreenRequest {
    id: string;
    reason: string;
}

/**
 * Canvas scale: never upscale, never wider than 1600 px and (D-SHOT, when the height is given) never more than about
 * 1600 x 900 px in total, so a big or tall viewport is drawn smaller.
 */
export function screenshotScale(viewportWidth: number, viewportHeight?: number): number {
    if (!viewportWidth || viewportWidth <= 0) {
        return 1;
    }
    let scale = Math.min(1, SCREENSHOT_MAX_WIDTH / viewportWidth);
    if (viewportHeight && viewportHeight > 0) {
        scale = Math.min(scale, Math.sqrt(SCREENSHOT_MAX_PIXELS / (viewportWidth * viewportHeight)));
    }
    return scale;
}

/**
 * D-SHOT: true for an element that lies entirely above or left of the viewport and can be drawn as an empty box of the
 * same size (its content is not cloned; its own size and colours are, so nothing visible moves). Not for fixed / sticky
 * elements, inline boxes (their text flows into visible lines) or table wrappers (the kept rows must keep their table).
 */
export function isHollowable(
    rect: {top: number; left: number; height: number; width: number},
    position?: string,
    display?: string,
    tagName?: string
): boolean {
    if (!rect || position === 'fixed' || position === 'sticky') {
        return false;
    }
    if (!display || display === 'none' || display === 'contents' || display.startsWith('inline')) {
        return false;
    }
    if (/^(table|thead|tbody|tfoot|colgroup|col|caption)$/i.test(tagName || '')) {
        return false;
    }
    return rect.top + rect.height <= -50 || rect.left + rect.width <= -50;
}

/**
 * D-SHOT: what to leave out of the picture, worked out in one pass over the live page before anything is cloned:
 * `drop` = elements not cloned at all (the chat, overlay backdrops, `display: none` parts, everything entirely below /
 * right of the viewport); `hollow` = elements entirely above / left of it, cloned as empty boxes of the same size.
 */
export interface ScreenPrune {
    drop: Set<Node>;
    hollow: Set<Node>;
    /** CU-14zefhy22vq R2: flex containers that lost a child to `drop` (their kept children must not grow into the gap) */
    flexParents?: Set<Element>;
}

type PruneVerdict = 'keep' | 'drop' | 'hollow';

function pruneVerdict(el: Element, viewport: {width: number; height: number}): PruneVerdict {
    if (isIgnoredForScreenshot(el)) {
        return 'drop';
    }
    const rect = el.getBoundingClientRect();
    const html = typeof HTMLElement !== 'undefined' && el instanceof HTMLElement;
    if (rect.width === 0 && rect.height === 0) {
        // hidden parts (menus, templates, closed dropdowns) are skipped; display: contents / empty wrappers stay
        return html && getComputedStyle(el).display === 'none' ? 'drop' : 'keep';
    }
    const bottom = rect.top + rect.height;
    const right = rect.left + rect.width;
    if (rect.top < viewport.height + 50 && rect.left < viewport.width + 50 && bottom > -50 && right > -50) {
        return 'keep'; // cheap common case: on screen, no style lookup
    }
    const style = getComputedStyle(el);
    if (isOffViewport(rect, viewport, style.position, el.tagName)) {
        return 'drop';
    }
    return html && isHollowable(rect, style.position, style.display, el.tagName) ? 'hollow' : 'keep';
}

/**
 * Builds the `ScreenPrune` for `root` (normally `document.body`); `root` itself is always kept. `allowHollow` = false
 * keeps content above the viewport whole (used when the window itself is scrolled: the picture is drawn from the top
 * of the page then, so that content is what shows).
 */
export function planScreenPrune(root: Element, viewport: {width: number; height: number}, allowHollow = true): ScreenPrune {
    const plan: ScreenPrune = {drop: new Set<Node>(), hollow: new Set<Node>(), flexParents: new Set<Element>()};
    const visit = (parent: Element): void => {
        const children = parent.children;
        let lostChild = false;
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            let verdict: PruneVerdict;
            try {
                verdict = pruneVerdict(child, viewport);
            } catch (e) {
                verdict = 'keep';
            }
            if (verdict === 'hollow' && (!allowHollow || hasStickyCell(child))) {
                // CU-14zefhy21rx H10: a header row whose cells are sticky shows on screen although its own box is above
                verdict = 'keep';
            }
            if (verdict === 'drop') {
                plan.drop.add(child);
                lostChild = true;
            } else if (verdict === 'hollow') {
                plan.hollow.add(child);
                if (/^tr$/i.test(child.tagName)) {
                    // a row keeps its cells (as empty boxes), so the table columns below keep their widths
                    for (let c = 0; c < child.children.length; c++) {
                        plan.hollow.add(child.children[c]);
                    }
                }
            } else {
                visit(child);
            }
        }
        if (lostChild && isFlexContainer(parent)) {
            plan.flexParents.add(parent);
        }
    };
    if (root) {
        visit(root);
    }
    return plan;
}

function isFlexContainer(el: Element): boolean {
    try {
        return /flex$/.test(getComputedStyle(el).display);
    } catch (e) {
        return false;
    }
}

/** CU-14zefhy22vq R2: attribute on a kept child of a flex container that lost children; its clone must not grow. */
export const NO_GROW_ATTRIBUTE = 'data-ai-shot-nogrow';
/** CU-14zefhy24jk X1: set on a live checked radio / checkbox so its clone can be given the `checked` attribute. */
export const CHECKED_ATTRIBUTE = 'data-ai-shot-checked';

/**
 * CU-14zefhy22vq R2: in the clone every flex item has its live size copied (width / height), but the items left out of the
 * picture (below / right of the viewport) free up room in a flex container, and the kept items with `flex-grow` > 0
 * (such as the dashboard's table sections) then grew into it and pushed the content down by hundreds of pixels. Marks the
 * kept children of each such container; the clone sets them to their copied size (`applyNoGrow`). Returns the function
 * that removes the marks from the live page.
 */
export function markFlexLosers(plan: ScreenPrune): () => void {
    const marked: Element[] = [];
    (plan.flexParents || new Set<Element>()).forEach((parent) => {
        for (let i = 0; i < parent.children.length; i++) {
            const child = parent.children[i];
            if (!plan.drop.has(child)) {
                child.setAttribute(NO_GROW_ATTRIBUTE, '');
                marked.push(child);
            }
        }
    });
    return () => marked.forEach((el) => el.removeAttribute(NO_GROW_ATTRIBUTE));
}

/** CU-14zefhy22vq R2: a marked clone keeps the size it has on screen (no grow, size from its copied width / height). */
export function applyNoGrow(clone: Element): void {
    if (!clone?.hasAttribute?.(NO_GROW_ATTRIBUTE)) {
        return;
    }
    clone.removeAttribute(NO_GROW_ATTRIBUTE);
    const style = (clone as HTMLElement).style;
    if (style) {
        style.setProperty('flex-grow', '0', 'important');
        style.setProperty('flex-basis', 'auto', 'important');
    }
}

/**
 * CU-14zefhy24jk X1: marks every checked radio / checkbox the picture keeps with `CHECKED_ATTRIBUTE`. The library copies only
 * an input's `value` attribute, and the picture is painted from the clone's attributes, so a `checked` that exists only as the
 * live property (Angular form binding, a user click) was lost: the header Order / Run radios showed neither selected.
 * Returns the function that removes the marks from the live page.
 */
export function markCheckedInputs(root: Element, plan: ScreenPrune): () => void {
    const marked: HTMLInputElement[] = [];
    if (root && typeof root.querySelectorAll === 'function') {
        root.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((el: HTMLInputElement) => {
            if (el.checked && !plan.drop.has(el) && !plan.hollow.has(el)) {
                el.setAttribute(CHECKED_ATTRIBUTE, '');
                marked.push(el);
            }
        });
    }
    return () => marked.forEach((el) => el.removeAttribute(CHECKED_ATTRIBUTE));
}

/** CU-14zefhy24jk X1: a cloned input that was checked on screen gets the `checked` attribute (what the picture is painted from). */
export function applyCheckedState(clone: Element): void {
    if (!clone?.hasAttribute?.(CHECKED_ATTRIBUTE)) {
        return;
    }
    clone.removeAttribute(CHECKED_ATTRIBUTE);
    clone.setAttribute('checked', '');
}

/** True for a table row with at least one `position: sticky` cell (such a row is never emptied). */
function hasStickyCell(el: Element): boolean {
    if (!/^tr$/i.test(el.tagName)) {
        return false;
    }
    for (let c = 0; c < el.children.length; c++) {
        if (getComputedStyle(el.children[c]).position === 'sticky') {
            return true;
        }
    }
    return false;
}

/**
 * CU-14zefhy21rx H10: how far a sticky element is stuck away from its normal place (live box minus normal-flow box),
 * rounded; null when it sits where it normally would. The clone is not scrolled (the scrolled pane's content is moved
 * with a transform instead), so without this a stuck header such as a list's column-header row lands above the picture.
 */
export function stuckOffset(live: {top: number; left: number}, normal: {top: number; left: number}): {dx: number; dy: number} | null {
    if (!live || !normal) {
        return null;
    }
    const dx = Math.round(live.left - normal.left);
    const dy = Math.round(live.top - normal.top);
    return dx || dy ? {dx, dy} : null;
}

/**
 * CU-14zefhy21rx H10: finds the sticky elements the picture keeps (not dropped / emptied by `plan`) that are stuck away
 * from their normal place and marks each with `STUCK_ATTRIBUTE` = `dx,dy`, which the clone copies (`applyStuckOffset`).
 * The normal place is read by making them static for one synchronous layout (nothing is painted in between). Returns
 * the function that removes the marks from the live page.
 */
export function markStuckElements(root: Element, plan: ScreenPrune): () => void {
    const sticky: HTMLElement[] = [];
    const visit = (parent: Element): void => {
        for (let i = 0; i < parent.children.length; i++) {
            const child = parent.children[i];
            if (plan.drop.has(child) || plan.hollow.has(child)) {
                continue;
            }
            if (child instanceof HTMLElement && getComputedStyle(child).position === 'sticky') {
                sticky.push(child);
            }
            visit(child);
        }
    };
    if (root) {
        visit(root);
    }
    const marked: HTMLElement[] = [];
    if (!sticky.length) {
        return () => undefined;
    }
    const live = sticky.map((el) => el.getBoundingClientRect());
    const inline = sticky.map((el) => ({value: el.style.getPropertyValue('position'), priority: el.style.getPropertyPriority('position')}));
    sticky.forEach((el) => el.style.setProperty('position', 'static', 'important'));
    const normal = sticky.map((el) => el.getBoundingClientRect());
    sticky.forEach((el, i) => {
        el.style.removeProperty('position');
        if (inline[i].value) {
            el.style.setProperty('position', inline[i].value, inline[i].priority);
        }
        const offset = stuckOffset(live[i], normal[i]);
        if (offset) {
            el.setAttribute(STUCK_ATTRIBUTE, `${offset.dx},${offset.dy}`);
            marked.push(el);
        }
    });
    return () => marked.forEach((el) => el.removeAttribute(STUCK_ATTRIBUTE));
}

/** CU-14zefhy21rx H10: puts a cloned stuck element where it shows on screen (relative offset) and drops the mark. */
export function applyStuckOffset(clone: Element): void {
    const mark = clone?.getAttribute?.(STUCK_ATTRIBUTE);
    if (mark === null || mark === undefined) {
        return;
    }
    clone.removeAttribute(STUCK_ATTRIBUTE);
    const [dx, dy] = mark.split(',').map((n) => Number(n));
    if (!Number.isFinite(dx) || !Number.isFinite(dy) || !(clone instanceof HTMLElement || (clone as any).style)) {
        return;
    }
    const style = (clone as HTMLElement).style;
    style.setProperty('position', 'relative', 'important');
    style.setProperty('top', `${dy}px`, 'important');
    style.setProperty('left', `${dx}px`, 'important');
    style.setProperty('bottom', 'auto', 'important');
    style.setProperty('right', 'auto', 'important');
    if (!style.zIndex || style.zIndex === 'auto') {
        style.setProperty('z-index', '3'); // stays above the rows it covers on screen
    }
}

/** `filter` for modern-screenshot: false for a node the plan leaves out (dropped, or inside an empty box). */
export function keepInScreenshot(node: Node, plan: ScreenPrune): boolean {
    if (!node) {
        return true;
    }
    if (plan.drop.has(node)) {
        return false;
    }
    const parent = node.parentNode;
    if (parent && plan.hollow.has(parent)) {
        // only the cells of an emptied row stay (they are emptied too)
        return plan.hollow.has(node);
    }
    return true;
}

/** Cached image data URLs (map stills, logos), shared by captures; small and bounded. */
const IMAGE_CACHE = new Map<string, string>();
const IMAGE_CACHE_MAX_ENTRIES = 60;
const IMAGE_CACHE_MAX_CHARS = 400 * 1024;

function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });
}

/**
 * D-SHOT: modern-screenshot `fetchFn` for one capture: each image is fetched at most once (cached across captures), gets
 * `timeoutMs`, and after `maxImages` fetches the rest are drawn blank, so a page full of pictures cannot stall the capture.
 * A failed / slow image is drawn blank instead of failing the picture.
 */
export function createImageFetcher(
    maxImages = SCREENSHOT_MAX_IMAGES,
    timeoutMs = SCREENSHOT_IMAGE_TIMEOUT_MS,
    fetchImpl: (url: string, init: RequestInit) => Promise<Response> = (url, init) => fetch(url, init)
): (url: string) => Promise<string> {
    let started = 0;
    return async (url: string): Promise<string> => {
        if (!url || url.startsWith('data:')) {
            return url || BLANK_IMAGE;
        }
        const cached = IMAGE_CACHE.get(url);
        if (cached) {
            return cached;
        }
        if (started >= maxImages) {
            return BLANK_IMAGE;
        }
        started++;
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        let timer: any;
        try {
            const response = await Promise.race([
                fetchImpl(url, {cache: 'force-cache', signal: controller?.signal}),
                new Promise<never>((_, reject) => {
                    timer = setTimeout(() => {
                        controller?.abort();
                        reject(new Error('image timed out'));
                    }, timeoutMs);
                })
            ]);
            if (!response || !response.ok) {
                return BLANK_IMAGE;
            }
            const dataUrl = await blobToDataUrl(await response.blob());
            if (!dataUrl) {
                return BLANK_IMAGE;
            }
            if (dataUrl.length <= IMAGE_CACHE_MAX_CHARS) {
                if (IMAGE_CACHE.size >= IMAGE_CACHE_MAX_ENTRIES) {
                    IMAGE_CACHE.delete(IMAGE_CACHE.keys().next().value);
                }
                IMAGE_CACHE.set(url, dataUrl);
            }
            return dataUrl;
        } catch (e) {
            return BLANK_IMAGE;
        } finally {
            clearTimeout(timer);
        }
    };
}

/** D-SHOT: embedded web-font CSS (`@font-face` rule → rule with data URLs), fetched once per page load, not per capture. */
const FONT_CSS_CACHE = new Map<string, string>();

/**
 * File name the server expects for an answered request (`screen-<id>.png|jpg`) or a camera-button shot
 * (`screen-<time>.png|jpg`, the name the chat's `screenshots[]` accepts — CU-14zefhy1qnh).
 */
export function screenFileName(requestId: string | null, mime: string, now = Date.now()): string {
    const ext = mime === 'image/jpeg' ? 'jpg' : 'png';
    return requestId ? `screen-${requestId}.${ext}` : `screen-${now}.${ext}`;
}

/** A usable request from a poll response: an id of safe characters and a short reason; null otherwise. */
export function toScreenRequest(raw: any): ScreenRequest | null {
    const id = raw && typeof raw.id === 'string' ? raw.id.trim() : '';
    if (!id || !/^[A-Za-z0-9_-]{1,64}$/.test(id)) {
        return null;
    }
    const reason = raw && typeof raw.reason === 'string' ? raw.reason.replace(/\s+/g, ' ').trim().substring(0, 200) : '';
    return {id, reason};
}

/** Elements left out of the picture: the assistant popover (and anything else opted out) plus overlay backdrops. */
export function isIgnoredForScreenshot(el: Element): boolean {
    if (!el || !el.classList) {
        return false;
    }
    return el.classList.contains('cdk-overlay-backdrop') || el.hasAttribute('data-ai-ignore');
}

const FIELD_TAGS = /^(INPUT|TEXTAREA|SELECT)$/;

/**
 * Whether a form control (or the wrapper that carries its `formcontrolname` / `field` name, e.g. `<di-input field="apiKey">`)
 * holds a credential by its NAME: name / formcontrolname / field / id (inputs only) / autocomplete, so a "show password"
 * toggle (a text input) and an API-key box are masked too (RMA-14).
 */
export function isSensitiveControl(el: Element): boolean {
    if (!el || typeof el.getAttribute !== 'function') {
        return false;
    }
    const isField = FIELD_TAGS.test(el.tagName || '');
    if (isField && String((el as HTMLInputElement).type || '').toLowerCase() === 'password') {
        return true;
    }
    const names = ['name', 'formcontrolname', 'field'];
    if (isField) {
        names.push('id', 'autocomplete', 'aria-label', 'placeholder');
    }
    return names.some((attr) => {
        const value = el.getAttribute(attr);
        return !!value && (isSensitiveName(value) || (attr === 'autocomplete' && /password/i.test(value)));
    });
}

/** Blanks password / secret-named inputs and `data-ai-private` content in the cloned page that is rendered (never the live page). */
export function maskPrivate(doc: Document | Element): void {
    if (!doc || typeof (doc as any).querySelectorAll !== 'function') {
        return;
    }
    // a wrapper (di-input field="apiKey") is blanked like a `data-ai-private` block, a bare control like a password input
    doc.querySelectorAll('input, textarea, select, [formcontrolname], [field], [name]').forEach((el: HTMLElement) => {
        if (!isSensitiveControl(el)) {
            return;
        }
        if (FIELD_TAGS.test(el.tagName)) {
            if (!(el instanceof HTMLSelectElement)) {
                (el as HTMLInputElement).value = '';
                el.setAttribute('value', '');
            }
            el.style.backgroundColor = '#9e9e9e';
            el.style.color = 'transparent';
        } else {
            el.setAttribute('data-ai-private', '');
        }
    });
    doc.querySelectorAll('[data-ai-private]').forEach((el: HTMLElement) => {
        if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
            el.value = '';
            el.setAttribute('value', '');
        }
        el.style.backgroundColor = '#9e9e9e';
        el.style.color = 'transparent';
        el.style.textShadow = 'none';
        el.querySelectorAll('*').forEach((child: HTMLElement) => {
            if (child instanceof HTMLInputElement || child instanceof HTMLTextAreaElement) {
                child.value = '';
                child.setAttribute('value', '');
            }
            child.style.color = 'transparent';
            child.style.visibility = 'hidden';
        });
    });
}

function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality?: number): Promise<Blob> {
    return new Promise((resolve, reject) =>
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the screenshot'))), mime, quality)
    );
}

function downscale(canvas: HTMLCanvasElement, factor: number): HTMLCanvasElement {
    const out = document.createElement('canvas');
    out.width = Math.max(1, Math.round(canvas.width * factor));
    out.height = Math.max(1, Math.round(canvas.height * factor));
    out.getContext('2d').drawImage(canvas, 0, 0, out.width, out.height);
    return out;
}

function nextFrame(): Promise<void> {
    return new Promise((resolve) => (typeof requestAnimationFrame === 'function' ? requestAnimationFrame(() => resolve()) : setTimeout(resolve, 16)));
}

/** Hides the chat (and other `data-ai-ignore` parts) on the real page while the picture is taken; returns the restore. */
function hideIgnored(): () => void {
    const hidden: Array<{el: HTMLElement; visibility: string}> = [];
    try {
        document.querySelectorAll('.hm-assistant, .cdk-overlay-backdrop').forEach((el: HTMLElement) => {
            hidden.push({el, visibility: el.style.visibility});
            el.style.visibility = 'hidden';
        });
    } catch (e) {
        // nothing hidden; the clone still leaves the chat out
    }
    return () => hidden.forEach((h) => (h.el.style.visibility = h.visibility));
}

/**
 * Captures the visible part of the app (what the user sees, minus the chat) as a JPEG (q 0.75, ≤ 1600 px wide,
 * ≤ 1 MB). Rejects with `ScreenshotTimeoutError` after 15 s. modern-screenshot is loaded on first use only.
 *
 * D-SHOT (populated lists took 19–35 s): what to leave out is planned in one pass before cloning (`planScreenPrune`:
 * hidden parts and everything off screen; content above the viewport becomes empty boxes), images get a short timeout,
 * a cap and a cache (`createImageFetcher`), the wait for still-loading page images is short, web-font CSS is embedded
 * once per page load, and big viewports are drawn at a lower scale.
 */
export function captureVisibleScreen(timeoutMs = SCREENSHOT_TIMEOUT_MS): Promise<{blob: Blob; mime: string}> {
    // RMA-33: one capture at a time. Two overlapping captures (the auto-share bypasses the chat's `capturing` flag) made the
    // second save the already-hidden chat as its "original" state and restore it hidden: the panel stayed invisible.
    const result = captureQueue.then(
        () => captureOnce(timeoutMs),
        () => captureOnce(timeoutMs)
    );
    captureQueue = result.catch(() => undefined);
    return result;
}

let captureQueue: Promise<unknown> = Promise.resolve();

async function captureOnce(timeoutMs: number): Promise<{blob: Blob; mime: string}> {
    const restore = hideIgnored();
    const maps = {current: null as MapSnapshots | null};
    const unmark = {current: null as (() => void) | null};
    // set once the capture is over (done, failed or timed out): a render still running must not touch the live page then
    const run = {aborted: false};
    try {
        await nextFrame();
        return await withTimeout(renderVisibleScreen(restore, maps, unmark, run), timeoutMs);
    } finally {
        run.aborted = true;
        restore();
        maps.current?.remove();
        unmark.current?.();
    }
}

/** Loads the screenshot library on first use (an object, so specs can make the load slow). */
export const SCREENSHOT_LIBRARY = {
    load: () => import('modern-screenshot')
};

/** Thrown inside a render that outlived its capture (timed out): it stops before it changes the live page. */
export class ScreenshotAbortedError extends Error {
    constructor() {
        super('The screenshot was given up');
        this.name = 'ScreenshotAbortedError';
    }
}

async function renderVisibleScreen(
    restore: () => void,
    maps: {current: MapSnapshots | null},
    unmark: {current: (() => void) | null},
    run: {aborted: boolean}
): Promise<{blob: Blob; mime: string}> {
    const viewport = {width: window.innerWidth, height: window.innerHeight};
    // MAP-HM: WebGL map canvases read back blank; put a HERE-captured still over each on-screen map first
    maps.current = overlayMapSnapshots(viewport.width, viewport.height);
    const [{createContext, domToCanvas}] = await Promise.all([SCREENSHOT_LIBRARY.load(), maps.current.ready]);
    // CU-14zefhy21rx H10/H12: the 15 s limit may have passed during that wait; the capture's cleanup has run already,
    // so marking sticky parts now would leave the marks on the live page
    if (run.aborted) {
        throw new ScreenshotAbortedError();
    }
    // D-SHOT: decide what is left out in one read-only pass over the page (no layout work interleaved with cloning)
    // (the app scrolls inside its content panes; a scrolled window is drawn from the top, so nothing above is emptied then)
    const windowScrolled = !!(window.scrollX || window.scrollY);
    const plan = planScreenPrune(document.body, viewport, !windowScrolled);
    // CU-14zefhy21rx H10: stuck sticky parts (a list's column-header row in a scrolled pane) keep their on-screen place
    const unmarkStuck = windowScrolled ? () => undefined : markStuckElements(document.body, plan);
    // CU-14zefhy22vq R2: kept children of a flex container that lost children must not grow into the freed room
    const unmarkFlex = markFlexLosers(plan);
    // CU-14zefhy24jk X1: checked radios / checkboxes keep their checked state in the clone
    const unmarkChecked = markCheckedInputs(document.body, plan);
    unmark.current = () => {
        unmarkStuck();
        unmarkFlex();
        unmarkChecked();
    };
    const context = await createContext(document.body, {
        width: viewport.width,
        height: viewport.height,
        scale: screenshotScale(viewport.width, viewport.height),
        backgroundColor: '#ffffff',
        // only the wait for pictures still loading on the page (lazy rows below the fold never finish) uses this
        timeout: SCREENSHOT_IMAGE_WAIT_MS,
        autoDestruct: true,
        fetchFn: createImageFetcher(),
        filter: (node: Node) => keepInScreenshot(node, plan),
        // CU-14zefhy21rx H10/H12: copy only the properties that change the picture (was every computed property)
        includeStyleProperties: [...SCREENSHOT_STYLE_PROPERTIES],
        // tables get their full height inlined; with the rows below the viewport left out, the kept rows would stretch
        onCloneEachNode: (clone: Node) => {
            if (clone.nodeType === 1) {
                if (/^(TABLE|TBODY)$/.test((clone as Element).tagName)) {
                    (clone as HTMLElement).style.setProperty('height', 'auto', 'important');
                }
                applyStuckOffset(clone as Element);
                applyNoGrow(clone as Element);
                applyCheckedState(clone as Element);
            }
        },
        onCloneNode: (clone: Node) => {
            maskPrivate(clone as Element);
            restore(); // the page is cloned: the chat can show again while the picture is painted
            maps.current?.remove(); // the clone keeps its own copy of the map stills
            unmark.current?.(); // the clone carries the sticky offsets now
        },
        // keep the web-font CSS embedded by this capture for the next ones (the context's own map is cleared afterwards)
        onEmbedNode: () => {
            context.fontCssTexts.forEach((css, rule) => FONT_CSS_CACHE.set(rule, css));
        },
        features: {restoreScrollPosition: true}
    });
    FONT_CSS_CACHE.forEach((css, rule) => context.fontCssTexts.set(rule, css));
    // font fetches and painting the final picture get the normal limit; the whole capture is capped by withTimeout
    context.timeout = 8000;
    if (run.aborted) {
        throw new ScreenshotAbortedError(); // timed out while the context was made: no clone of the page is needed now
    }
    const canvas: HTMLCanvasElement = await domToCanvas(context);
    let current = canvas;
    for (const quality of [SCREENSHOT_JPEG_QUALITY, 0.65, 0.5]) {
        const jpeg = await canvasToBlob(current, 'image/jpeg', quality);
        if (jpeg.size <= SCREENSHOT_MAX_BYTES) {
            return {blob: jpeg, mime: 'image/jpeg'};
        }
    }
    // still too big (huge, busy screen): shrink until it fits
    for (let i = 0; i < 4; i++) {
        current = downscale(current, 0.75);
        const jpeg = await canvasToBlob(current, 'image/jpeg', 0.7);
        if (jpeg.size <= SCREENSHOT_MAX_BYTES) {
            return {blob: jpeg, mime: 'image/jpeg'};
        }
    }
    throw new Error('The screenshot is too large to share');
}
